import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import DataTable, { ColumnDef } from "@/components/utils/DataTable";
import { pageStyles } from "@/components/utils/PageLayout";
import { Button, Note, Icon, FilterSelect, loadingStats } from "@/components/ui";
import type { StatItem, FilterOption } from "@/components/ui";
import { cashEnvironmentAPI, supportingDataApi } from "@/services/api";
import { formatCurrency, formatDate, formatNumber } from "@/utils/formatters";
import { sumBy } from "@/utils/aggregate";

type CashRow = {
    client_id: number;
    client_code: string;
    client_name: string;
    scheme: string;
    aum: number;
    bank_balance: number;
    liquid_amt: number;
    clearing_cash: number;
    bank_balance_liquid_pct: number | null;
    sell_amount: number;
    amt_redeem: number;
    net_liquid: number;
    net_cash: number;
};

type CashEnvironmentResponse = {
    holding_date: string;
    to_be_invested: number;
    to_be_liquidated: number;
    scheme_warnings: string[];
    data: CashRow[];
};

// "t0" is not a third band: a same-day-settling scheme's liquid ETF is spendable
// cash, so it targets no buffer and bypasses the per-scheme tier rules.
type CashEnv = "0" | "1" | "t0";
type ViewType = "figure" | "percent";

const CASH_ENV_OPTIONS: FilterOption[] = [
    { value: "0", label: "Low Cash (Invest)" },
    { value: "1", label: "High Cash (Redeem)" },
    { value: "t0", label: "T+0 (Same-day Settlement)" },
];

// Named after the environment the rows were fetched under, not the one selected.
const CASH_ENV_FILE_LABEL: Record<CashEnv, string> = {
    "0": "low_cash",
    "1": "high_cash",
    t0: "t0_same_day",
};

const VIEW_OPTIONS: FilterOption[] = [
    { value: "figure", label: "Figure" },
    { value: "percent", label: "Percent of AUM" },
];

/** Figure columns, in table order. Drives the on-screen totals row. */
const AMOUNT_KEYS = [
    "bank_balance", "liquid_amt", "clearing_cash", "sell_amount",
    "aum", "amt_redeem", "net_liquid", "net_cash",
] as const;
type AmountKey = typeof AMOUNT_KEYS[number];

const PAGE_SIZE_OPTIONS = [20, 50, 100];

const CashEnvironment: React.FC = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [data, setData] = useState<CashEnvironmentResponse | null>(null);
    const [cashEnv, setCashEnv] = useState<CashEnv>("0");
    const [viewType, setViewType] = useState<ViewType>("figure");
    // The view the rows on screen were fetched under. Percent-of-AUM figures do not
    // add up across clients, so the totals row only appears for a figure view that
    // has actually been loaded — not for one merely selected in the panel.
    const [appliedView, setAppliedView] = useState<ViewType>("figure");
    const [appliedEnv, setAppliedEnv] = useState<CashEnv>("0");
    const [searchTerm, setSearchTerm] = useState("");
    const [sortBy, setSortBy] = useState<keyof CashRow>("amt_redeem");
    const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
    const [schemes, setSchemes] = useState<{ id: number; code?: string | null }[]>([]);
    const [schemeFilter, setSchemeFilter] = useState<string>("exclude");
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [liquidityDownloading, setLiquidityDownloading] = useState(false);

    // The two leading entries are filter modes the backend understands, not schemes.
    const schemeOptions: FilterOption[] = useMemo(() => [
        { value: "exclude", label: "All except SCPL and SIML" },
        { value: "0", label: "All" },
        ...schemes.map(s => {
            const code = (s.code || "").toString();
            return { value: code, label: code };
        }),
    ], [schemes]);

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await cashEnvironmentAPI.getCashEnvironment({
                cash_env: cashEnv,
                view_type: viewType,
                scheme_filter: schemeFilter,
            });
            setData(res);
            setAppliedView(viewType);
            setAppliedEnv(cashEnv);
            setCurrentPage(1);
        } catch (e) {
            const apiErr = e as { response?: { data?: { error?: string } }; message?: string };
            setError(apiErr?.response?.data?.error || apiErr?.message || "Failed to fetch cash environment data.");
            setData(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        let cancelled = false;
        supportingDataApi.getSchemes()
            .then(result => { if (!cancelled) setSchemes(Array.isArray(result) ? result : []); })
            .catch(() => { /* the scheme list is a convenience; the page works without it */ });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSort = (field: string) => {
        const f = field as keyof CashRow;
        if (sortBy === f) {
            setSortDir(prev => (prev === "asc" ? "desc" : "asc"));
        } else {
            setSortBy(f);
            setSortDir("desc");
        }
        setCurrentPage(1);
    };

    const filteredAndSorted = useMemo(() => {
        if (!data) return [] as CashRow[];
        const query = searchTerm.toLowerCase();

        const result = data.data.filter(r =>
            (r.client_name || "").toLowerCase().includes(query) ||
            (r.client_code || "").toLowerCase().includes(query)
        );

        return [...result].sort((a, b) => {
            let valA: any = a[sortBy];
            let valB: any = b[sortBy];
            if (sortBy === "client_name" || sortBy === "client_code" || sortBy === "scheme") {
                valA = String(valA ?? "").toLowerCase();
                valB = String(valB ?? "").toLowerCase();
            } else {
                valA = Number(valA) || 0;
                valB = Number(valB) || 0;
            }
            if (valA < valB) return sortDir === "asc" ? -1 : 1;
            if (valA > valB) return sortDir === "asc" ? 1 : -1;
            return 0;
        });
    }, [data, searchTerm, sortBy, sortDir]);

    const totalCount = filteredAndSorted.length;
    const totalPages = Math.ceil(totalCount / pageSize);
    const paginatedData = useMemo(
        () => filteredAndSorted.slice((currentPage - 1) * pageSize, currentPage * pageSize),
        [filteredAndSorted, currentPage, pageSize],
    );

    // Percent-of-AUM rows are shares, not rupees — render them as percentages and
    // leave them out of the totals row.
    const showTotals = appliedView === "figure";
    const fmtAmount = (value: number): string =>
        appliedView === "percent" ? `${formatNumber(value || 0, 2)}%` : formatNumber(value || 0, 2);

    const handleDownloadLiquidityFile = async () => {
        setLiquidityDownloading(true);
        try {
            const { blob, filename } = await cashEnvironmentAPI.downloadLiquidityFile();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setTimeout(() => window.URL.revokeObjectURL(url), 100);
        } catch (err: any) {
            let msg = "Failed to download liquidity file";
            const payload = err?.response?.data;
            if (payload instanceof Blob) {
                try {
                    msg = JSON.parse(await payload.text())?.error ?? msg;
                } catch {
                    // parse failed, keep default message
                }
            } else if (payload?.error) {
                msg = payload.error;
            }
            toast.error(msg);
        } finally {
            setLiquidityDownloading(false);
        }
    };

    // Mirrors the on-screen column order. The export carries data rows only — a
    // totals row would be double-counted by anything that sums the file.
    const csvColumns: { header: string; value: (r: CashRow) => string | number }[] = [
        { header: "Client Code", value: r => r.client_code || "" },
        { header: "Full Name", value: r => r.client_name || "" },
        { header: "Scheme", value: r => r.scheme || "" },
        { header: "Cash", value: r => r.bank_balance ?? 0 },
        { header: "Liquid", value: r => r.liquid_amt ?? 0 },
        { header: "Clearing Cash", value: r => r.clearing_cash ?? 0 },
        { header: "Sell Amount", value: r => r.sell_amount ?? 0 },
        { header: "Market Value", value: r => r.aum ?? 0 },
        { header: "Amount Redeem", value: r => r.amt_redeem ?? 0 },
        { header: "Net Liquid", value: r => r.net_liquid ?? 0 },
        { header: "Net Cash", value: r => r.net_cash ?? 0 },
    ];

    const downloadCSV = () => {
        if (!filteredAndSorted.length) {
            toast.error("No data to download");
            return;
        }
        const esc = (v: string | number) =>
            typeof v === "number" ? String(v) : `"${v.replace(/"/g, '""')}"`;
        const rows = [
            csvColumns.map(c => esc(c.header)).join(","),
            ...filteredAndSorted.map(r => csvColumns.map(c => esc(c.value(r))).join(",")),
        ];

        const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `cash_environment_${CASH_ENV_FILE_LABEL[appliedEnv]}_${new Date().toISOString().split("T")[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
    };

    // Every column carries a width: `stickyColumns` derives its pinned offsets from
    // them, and zero-width columns would stack on top of each other.
    const amountColumn = (key: AmountKey, header: string, width: string): ColumnDef<CashRow> => ({
        key,
        header,
        sortable: true,
        align: "right",
        width,
        render: row => fmtAmount(row[key]),
        ...(showTotals ? { renderFooter: (rows: CashRow[]) => formatNumber(sumBy(rows, key), 2) } : {}),
    });

    const columns: ColumnDef<CashRow>[] = [
        {
            key: "client_code",
            header: "Client Code",
            sortable: true,
            width: "130px",
            render: row => <span className={pageStyles['cell-strong']}>{row.client_code}</span>,
            ...(showTotals ? { renderFooter: () => "Totals" } : {}),
        },
        {
            key: "client_name",
            header: "Full Name",
            sortable: true,
            width: "220px",
            render: row => row.client_name,
            ...(showTotals ? {
                renderFooter: (rows: CashRow[]) => (
                    <span className={pageStyles['cell-sub']}>
                        {rows.length} client{rows.length === 1 ? "" : "s"} (all pages)
                    </span>
                ),
            } : {}),
        },
        { key: "scheme", header: "Scheme", sortable: true, width: "110px", render: row => row.scheme },
        amountColumn("bank_balance", "Cash", "130px"),
        amountColumn("liquid_amt", "Liquid", "130px"),
        amountColumn("clearing_cash", "Clearing Cash", "140px"),
        amountColumn("sell_amount", "Sell Amount", "130px"),
        amountColumn("aum", "Market Value", "140px"),
        amountColumn("amt_redeem", "Amount Redeem", "150px"),
        amountColumn("net_liquid", "Net Liquid", "130px"),
        amountColumn("net_cash", "Net Cash", "130px"),
    ];

    const stats: StatItem[] | undefined = loading
        ? loadingStats(["Holding date", "To be invested", "To be liquidated"])
        : data ? [
        { label: "Holding date", value: data.holding_date ? formatDate(data.holding_date) : "—" },
        {
            label: "To be invested",
            value: formatCurrency(data.to_be_invested),
            hint: cashEnv === "0" ? "Low cash environment" : undefined,
        },
        {
            label: "To be liquidated",
            value: formatCurrency(data.to_be_liquidated),
            hint: cashEnv === "1" ? "High cash environment" : undefined,
        },
    ] : undefined;

    const filterSlot = (
        <>
            <FilterSelect
                label="Schemes"
                options={schemeOptions}
                value={schemeOptions.find(o => o.value === schemeFilter) ?? null}
                onChange={opt => setSchemeFilter(opt?.value ?? "exclude")}
            />

            <FilterSelect
                label="Cash Environment"
                options={CASH_ENV_OPTIONS}
                value={CASH_ENV_OPTIONS.find(o => o.value === cashEnv) ?? null}
                onChange={opt => setCashEnv((opt?.value ?? "0") as CashEnv)}
            />

            <FilterSelect
                label="View"
                options={VIEW_OPTIONS}
                value={VIEW_OPTIONS.find(o => o.value === viewType) ?? null}
                onChange={opt => setViewType((opt?.value ?? "figure") as ViewType)}
            />

            <div className={pageStyles['filter-action']}>
                <Button variant="primary" onClick={fetchData} disabled={loading}>
                    {loading ? "Loading…" : "Search"}
                </Button>
            </div>
        </>
    );

    const warnings = data?.scheme_warnings ?? [];
    const toolbarSlot = warnings.length > 0 ? (
        <Note tone="warning">
            {warnings.length === 1 ? warnings[0] : (
                <ul>
                    {warnings.map((warning, idx) => <li key={idx}>{warning}</li>)}
                </ul>
            )}
        </Note>
    ) : undefined;

    return (
        <DataTable
            title="Cash Environment"
            subtitle={appliedView === "percent"
                ? "Cash, liquid and redemption positions as a percentage of each client's AUM"
                : "Cash, liquid and redemption positions per client"}
            stats={stats}
            data={paginatedData}
            footerRows={filteredAndSorted}
            columns={columns}
            rowKey={row => row.client_id}
            totalCount={totalCount}
            loading={loading}
            loadingMessage="Loading cash environment…"
            error={error}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            pageSize={pageSize}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            onPageSizeChange={size => { setPageSize(size); setCurrentPage(1); }}
            sortField={sortBy}
            sortDir={sortDir}
            onSort={handleSort}
            searchQuery={searchTerm}
            onSearchChange={value => { setSearchTerm(value); setCurrentPage(1); }}
            searchPlaceholder="Search by client name or code…"
            filterSlot={filterSlot}
            toolbarSlot={toolbarSlot}
            onDownloadCSV={downloadCSV}
            headerActions={
                <Button
                    onClick={handleDownloadLiquidityFile}
                    disabled={liquidityDownloading}
                    icon={<Icon name="download" size={14} />}
                >
                    {liquidityDownloading ? "Downloading…" : "Liquidity File"}
                </Button>
            }
            emptyMessage="No clients found for the selected cash environment."
            compact
            stickyColumns={2}
            fitContent
        />
    );
};

export default CashEnvironment;
